import React, { useState } from 'react';
import { experience, education, certifications } from '../data/portfolio';
import './Experience.css';

const Experience = () => {
  const [activeTab, setActiveTab] = useState('experience');

  const tabs = [
    { id: 'experience', label: 'Experience', count: experience.length },
    { id: 'education', label: 'Education', count: education.length },
    { id: 'certifications', label: 'Certifications', count: certifications.length },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Present';
    const [year, month] = dateStr.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const renderExperience = () => experience.map((job) => (
    <article key={job.id} className="timeline__item">
      <div className="timeline__marker" aria-hidden="true"><div className="timeline__dot" /></div>
      <div className="timeline__content">
        <header className="timeline__header">
          <div className="timeline__meta">
            <span className="timeline__company">{job.company}</span>
            <span className="timeline__location">{job.location}</span>
          </div>
          <time className="timeline__date">
            {formatDate(job.startDate)} – {job.current ? 'Present' : formatDate(job.endDate)}
          </time>
        </header>
        <h3 className="timeline__title">{job.position}</h3>
        <p className="timeline__description">{job.description}</p>
        <ul className="timeline__achievements" aria-label="Key achievements">
          {job.achievements.map((achievement, index) => (
            <li key={index} className="timeline__achievement">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {achievement}
            </li>
          ))}
        </ul>
        <div className="timeline__tech" aria-label="Technologies used">
          {job.techStack.map((tech, index) => <span key={index} className="badge badge--tech">{tech}</span>)}
        </div>
      </div>
    </article>
  ));

  const renderEducation = () => education.map((edu) => (
    <article key={edu.id} className="timeline__item timeline__item--education">
      <div className="timeline__marker" aria-hidden="true"><div className="timeline__dot timeline__dot--education" /></div>
      <div className="timeline__content">
        <header className="timeline__header">
          <div className="timeline__meta">
            <span className="timeline__company">{edu.school}</span>
            <span className="timeline__location">{edu.location}</span>
          </div>
          <time className="timeline__date">{edu.graduationYear}</time>
        </header>
        <h3 className="timeline__title">{edu.degree}</h3>
        {edu.honors && (
          <p className="timeline__honors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            {edu.honors}
          </p>
        )}
      </div>
    </article>
  ));

  const renderCertifications = () => certifications.map((cert) => (
    <article key={cert.id} className="timeline__item timeline__item--certification">
      <div className="timeline__marker" aria-hidden="true"><div className="timeline__dot timeline__dot--certification" /></div>
      <div className="timeline__content">
        <header className="timeline__header">
          <div className="timeline__meta"><span className="timeline__company">{cert.issuer}</span></div>
          <time className="timeline__date">{cert.year}</time>
        </header>
        <h3 className="timeline__title">{cert.name}</h3>
        <p className="timeline__credential">Credential ID: {cert.credentialId}</p>
      </div>
    </article>
  ));

  return (
    <section id="experience" className="experience section" aria-labelledby="experience-title">
      <div className="container">
        <header className="section-header" data-eyebrow="Experience">
          <h2 id="experience-title" className="section-title">Experience</h2>
          <p className="section-subtitle">My journey building mobile applications</p>
        </header>

        <div className="experience__tabs" role="tablist" aria-label="Experience categories">
          {tabs.map((tab) => (
            <button
              key={tab.id} role="tab" aria-selected={activeTab === tab.id}
              aria-controls={`${tab.id}-panel`} id={`${tab.id}-tab`}
              className={`experience__tab ${activeTab === tab.id ? 'experience__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              <span className="experience__tab-count">{tab.count}</span>
            </button>
          ))}
        </div>

        <div className="experience__panels">
          <div role="tabpanel" id="experience-panel" aria-labelledby="experience-tab" hidden={activeTab !== 'experience'} className="experience__panel">
            <div className="timeline">{renderExperience()}</div>
          </div>

          <div role="tabpanel" id="education-panel" aria-labelledby="education-tab" hidden={activeTab !== 'education'} className="experience__panel">
            {education.length > 0 ? <div className="timeline">{renderEducation()}</div> : (
              <p className="experience__empty">No education entries added yet.</p>
            )}
          </div>

          <div role="tabpanel" id="certifications-panel" aria-labelledby="certifications-tab" hidden={activeTab !== 'certifications'} className="experience__panel">
            {certifications.length > 0 ? <div className="timeline">{renderCertifications()}</div> : (
              <p className="experience__empty">No certifications added yet.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
